import { axiosInstance } from '@/lib/axios';
import type {
  AccountDetail,
  AccountMilo,
  AccountMiloInput,
  AccountRow,
  AccountsView,
  DateRange,
  DeviceAccumulatorsInput,
  DeviceDetail,
  DeviceRow,
  DeviceSettingsInput,
  DevicesView,
  FleetResult,
  GroupDetail,
  GroupsView,
  HealthView,
  LedgerStatus,
  LedgerView,
  LinkInput,
  MiloAccessRow,
  MiloConversation,
  MiloConversationFilters,
  MiloConversationList,
  MiloCustomerInput,
  MiloCustomerRow,
  MiloFailedTurn,
  MiloInsights,
  MiloKeyTest,
  MiloProvider,
  MiloSettings,
  MiloSettingsInput,
  MiloUsage,
  RebuildInput,
  SimDaysView,
  SimRunView,
  SimsView,
  ZonesView,
} from './types';

const BASE = '/api/fleet';

/** A read's `{ data, warnings }`, with `warnings` always an array. */
function read<T>(body: { data: T; warnings?: string[] }): FleetResult<T> {
  return { data: body.data, warnings: body.warnings ?? [] };
}

/** A write's answer: its `data` when echo wraps it, the body itself when it does not. */
function written<T>(body: unknown): T {
  if (body !== null && typeof body === 'object' && 'data' in body) {
    return (body as { data: T }).data;
  }
  return body as T;
}

const get = async <T>(url: string): Promise<FleetResult<T>> => {
  const { data } = await axiosInstance.get<{ data: T; warnings?: string[] }>(url);
  return read(data);
};

const put = async <T>(url: string, body: unknown): Promise<T> => {
  const { data } = await axiosInstance.put(url, body);
  return written<T>(data);
};

/** `?a=1&b=x` from the defined, non-empty values only; `''` when there are none. */
export function queryString(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : '';
}

/* ── Accounts ─────────────────────────────────────────────────────────── */

export const getAccounts = () => get<AccountsView>(`${BASE}/accounts`);
export const getAccount = (id: number) => get<AccountDetail>(`${BASE}/accounts/${id}`);
export const setAccountDisabled = (id: number, disabled: boolean) =>
  put<AccountRow>(`${BASE}/accounts/${id}/disabled`, { disabled });
export const setAccountMilo = (id: number, input: AccountMiloInput) =>
  put<AccountMilo>(`${BASE}/accounts/${id}/milo`, input);

export const addLink = async (input: LinkInput): Promise<void> => {
  await axiosInstance.post(`${BASE}/links`, input);
};
export const removeLink = async (input: LinkInput): Promise<void> => {
  await axiosInstance.delete(`${BASE}/links`, { data: input });
};

/* ── Vehicles ─────────────────────────────────────────────────────────── */

export const getDevices = () => get<DevicesView>(`${BASE}/devices`);
export const getDevice = (id: number) => get<DeviceDetail>(`${BASE}/devices/${id}`);
export const setDeviceSettings = (id: number, input: DeviceSettingsInput) =>
  put<DeviceRow>(`${BASE}/devices/${id}/settings`, input);
export const setDeviceAccumulators = (id: number, input: DeviceAccumulatorsInput) =>
  put<DeviceRow>(`${BASE}/devices/${id}/accumulators`, input);
export const setDeviceGroup = (id: number, groupId: number | null) =>
  put<DeviceRow>(`${BASE}/devices/${id}/group`, { groupId });

/* ── Groups and zones ─────────────────────────────────────────────────── */

export const getGroups = () => get<GroupsView>(`${BASE}/groups`);
export const getGroup = (id: number) => get<GroupDetail>(`${BASE}/groups/${id}`);
export const getZones = () => get<ZonesView>(`${BASE}/zones`);

/* ── Ledger and health ────────────────────────────────────────────────── */

export const getLedger = (range?: DateRange) =>
  get<LedgerView>(`${BASE}/ledger${queryString({ from: range?.from, to: range?.to })}`);
export const startRebuild = async (input: RebuildInput): Promise<LedgerStatus> => {
  const { data } = await axiosInstance.post(`${BASE}/ledger/rebuild`, input);
  return written<LedgerStatus>(data);
};
export const getHealth = () => get<HealthView>(`${BASE}/health`);

/* ── SIM data (1NCE) ──────────────────────────────────────────────────── */

const rangeQuery = (range: DateRange) => queryString({ from: range.from, to: range.to });
export const getSims = (range: DateRange) => get<SimsView>(`${BASE}/sims${rangeQuery(range)}`);
export const getSimsLive = (range: DateRange) => get<SimsView>(`${BASE}/sims/live${rangeQuery(range)}`);
export const getSimDays = (iccid: string, range: DateRange) =>
  get<SimDaysView>(`${BASE}/sims/${encodeURIComponent(iccid)}/days${rangeQuery(range)}`);
export const startSimSync = async (): Promise<SimRunView> => {
  const { data } = await axiosInstance.post(`${BASE}/sims/sync`);
  return written<SimRunView>(data);
};

/* ── GeoPoint AI ──────────────────────────────────────────────────────── */

export const getMiloSettings = () => get<MiloSettings>(`${BASE}/milo/settings`);
export const saveMiloSettings = (input: MiloSettingsInput) => put<MiloSettings>(`${BASE}/milo/settings`, input);
export const testMiloKey = async (provider: MiloProvider): Promise<MiloKeyTest> => {
  const { data } = await axiosInstance.post(`${BASE}/milo/test`, { provider });
  return written<MiloKeyTest>(data);
};
export const getMiloAccess = () => get<MiloAccessRow[]>(`${BASE}/milo/access`);
export const getMiloCustomers = () => get<MiloCustomerRow[]>(`${BASE}/milo/customers`);
export const setMiloCustomer = (customerId: number, input: MiloCustomerInput) =>
  put<MiloCustomerRow>(`${BASE}/milo/customers/${customerId}`, input);
export const getMiloUsage = (month?: string) => get<MiloUsage>(`${BASE}/milo/usage${queryString({ month })}`);
export const getMiloConversations = (filters: MiloConversationFilters, cursor?: string | null) =>
  get<MiloConversationList>(
    `${BASE}/milo/conversations${queryString({
      userId: filters.userId,
      from: filters.from,
      to: filters.to,
      status: filters.status && filters.status !== 'all' ? filters.status : undefined,
      tool: filters.tool,
      feedback: filters.feedback,
      cursor,
    })}`
  );
export const getMiloConversation = (id: string) =>
  get<MiloConversation>(`${BASE}/milo/conversations/${encodeURIComponent(id)}`);
export const getMiloFailedTurn = (id: string) =>
  get<MiloFailedTurn>(`${BASE}/milo/failed-turns/${encodeURIComponent(id)}`);
export const getMiloInsights = (range: DateRange) =>
  get<MiloInsights>(`${BASE}/milo/insights${queryString({ from: range.from, to: range.to })}`);
